import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, CheckCircle2, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WizardStepper } from '@/components/custom/WizardStepper';
import { AddressPickerTable } from '@/components/custom/AddressPickerTable';
import { addressSources, addressActions, addressStatuses, addressScopes } from '@/lib/mock-data';
import { createTicketWithExtras, fetchDmpAddress, fetchDmpAddressesByBuildingKey, fetchBuildingVolumes, fetchJvidVolumes, searchDmpAddresses } from '@/hooks/useTickets';
import { AttachmentPicker } from '@/components/tickets/AttachmentPicker';
import { SimilarTicketsBanner, SimilarTicketsConfirmDialog } from '@/components/tickets/SimilarTicketsBanner';
import type { DmpAddressResponse, BuildingGroupEntry, SimilarTicketItem } from '@uniticket/shared';

export function AddressWizard() {
  const { t } = useTranslation(['tickets', 'common']);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Classification fields (PRD 4.2.5)
  const [source, setSource] = useState('');
  const [action, setAction] = useState('');
  const [addressStatus, setAddressStatus] = useState('');
  const [scope, setScope] = useState('');

  // JVID + DMP fetch state
  const [jvid, setJvid] = useState('');
  const [dmpFetched, setDmpFetched] = useState(false);
  const [dmpRefOpen, setDmpRefOpen] = useState(false);
  const [dmpOriginal, setDmpOriginal] = useState<DmpAddressResponse | null>(null);
  const [fetchingDmp, setFetchingDmp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [similarResults, setSimilarResults] = useState<SimilarTicketItem[]>([]);
  const [showSimilarConfirm, setShowSimilarConfirm] = useState(false);

  // Building key lookup state
  const [buildingKeyInput, setBuildingKeyInput] = useState('');
  const [fetchingBuilding, setFetchingBuilding] = useState(false);
  const [buildingAddresses, setBuildingAddresses] = useState<DmpAddressResponse[]>([]);
  const [buildingFetched, setBuildingFetched] = useState(false);

  // Building group state
  const [buildingGroupEntries, setBuildingGroupEntries] = useState<BuildingGroupEntry[]>([]);
  const [sduDefaultValue, setSduDefaultValue] = useState<{ volumeMdu: number; existingBoxes: string } | null>(null);
  const [loadingBuildingData, setLoadingBuildingData] = useState(false);

  // Address search state
  const [searchStreet, setSearchStreet] = useState('');
  const [searchPostalCode, setSearchPostalCode] = useState('');
  const [searchHouseNumber, setSearchHouseNumber] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressSearchResults, setAddressSearchResults] = useState<DmpAddressResponse[]>([]);
  const [addressSearchError, setAddressSearchError] = useState('');

  // Group 1 — Identifiers
  const [belmapAddressId, setBelmapAddressId] = useState('');
  const [belmapBuildingId, setBelmapBuildingId] = useState('');

  // Group 2 — Address
  const [streetName, setStreetName] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [houseNumberExt, setHouseNumberExt] = useState('');
  const [busNumber, setBusNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [city, setCity] = useState('');

  // Group 3 — Coordinates
  const [xLambert, setXLambert] = useState('');
  const [yLambert, setYLambert] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');

  // Group 4 — Building/Property
  const [volumeMdu, setVolumeMdu] = useState('');
  const [existingBoxes, setExistingBoxes] = useState('');
  const [dwellingUnitType, setDwellingUnitType] = useState('');
  const [addressSource, setAddressSource] = useState('');

  // Ticket fields
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');

  // Customer contact
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactOpen, setContactOpen] = useState(false);

  // Customer email capture (files + pasted text) — both forwarded after ticket create
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [pastedEmail, setPastedEmail] = useState('');

  // PRD 4.2.5 business rule: Source="Other" → Action locked to "Information"
  useEffect(() => {
    if (source === 'Other') {
      setAction('Information');
    }
  }, [source]);

  // Reset building key + DMP + building group state when action changes
  useEffect(() => {
    setBuildingKeyInput('');
    setFetchingBuilding(false);
    setBuildingAddresses([]);
    setBuildingFetched(false);
    setDmpFetched(false);
    setDmpOriginal(null);
    setJvid('');
    // Reset building group state
    setBuildingGroupEntries([]);
    setSduDefaultValue(null);
    setLoadingBuildingData(false);
    // Reset address search state
    setSearchStreet('');
    setSearchPostalCode('');
    setSearchHouseNumber('');
    setAddressSearchResults([]);
    setAddressSearchError('');
  }, [action]);

  /** Populate all 17 field states from a DmpAddressResponse */
  function populateFieldsFromDmpAddress(d: DmpAddressResponse) {
    setJvid(String(d.jvid));
    setBelmapAddressId(d.belmapAddressId != null ? String(d.belmapAddressId) : '');
    setBelmapBuildingId(d.belmapBuildingId != null ? String(d.belmapBuildingId) : '');
    setStreetName(d.streetName ?? '');
    setHouseNumber(d.houseNumber ?? '');
    setHouseNumberExt(d.houseNumberExtension ?? '');
    setBusNumber(d.busNumber ?? '');
    setPostalCode(d.postalCode ?? '');
    setMunicipality(d.municipality ?? '');
    setCity(d.city ?? '');
    setXLambert(d.x != null ? String(d.x) : '');
    setYLambert(d.y != null ? String(d.y) : '');
    setLat(d.lat != null ? String(d.lat) : '');
    setLon(d.lon != null ? String(d.lon) : '');
    setVolumeMdu(d.volumeMdu != null ? String(d.volumeMdu) : '');
    setExistingBoxes(d.existingBoxes ?? '');
    setDwellingUnitType(d.dwellingUnitType ?? '');
    setAddressSource(d.addressSource ?? '');
  }

  async function handleDmpFetch() {
    if (jvid.length < 8) return;
    setFetchingDmp(true);
    try {
      const res = await fetchDmpAddress(jvid);
      const d = res.data;
      setDmpOriginal(d);
      populateFieldsFromDmpAddress(d);
      setDmpFetched(true);
      // Auto-compute building group volumes via JVID endpoint (backend handles everything)
      fetchJvidVolumes(String(d.jvid)).then((volRes) => {
        const { entries, sduDefault: sduDef } = volRes.data;
        setBuildingGroupEntries(entries);
        setSduDefaultValue(sduDef);
        if (entries.length > 0) {
          fillVolumeFromEntries(entries, d);
        } else if (sduDef) {
          setVolumeMdu(String(sduDef.volumeMdu));
          if (sduDef.existingBoxes) setExistingBoxes(sduDef.existingBoxes);
        }
      }).catch(() => {
        // Volume computation failed — no default applied, user can fill manually
      });
    } catch {
      toast.error(t('tickets:addressWizard.buildingNotFound'));
      setDmpFetched(false);
      setDmpOriginal(null);
    } finally {
      setFetchingDmp(false);
    }
  }

  async function handleBuildingKeyFetch() {
    if (!buildingKeyInput.trim()) return;
    const bKey = buildingKeyInput.trim();
    setFetchingBuilding(true);
    setLoadingBuildingData(true);
    setBuildingAddresses([]);
    setBuildingFetched(false);
    setBuildingGroupEntries([]);
    try {
      const res = await fetchDmpAddressesByBuildingKey(bKey);
      setBuildingAddresses(res.data.addresses);
      setBuildingFetched(true);
      if (res.data.addresses.length === 0) {
        toast.error(t('tickets:addressWizard.noAddressesForBuilding'));
      } else {
        // Auto-compute volumes for all addresses
        await autoComputeVolumes(bKey);
      }
    } catch {
      toast.error(t('tickets:addressWizard.buildingNotFound'));
      setBuildingAddresses([]);
      setBuildingFetched(false);
    } finally {
      setFetchingBuilding(false);
      setLoadingBuildingData(false);
    }
  }

  function handleAddressSelect(address: DmpAddressResponse) {
    setDmpOriginal(address);
    populateFieldsFromDmpAddress(address);
    setDmpFetched(true);
    // Auto-fill volume from matching building group entry, or apply SDU default from backend
    if (buildingGroupEntries.length > 0) {
      fillVolumeFromEntries(buildingGroupEntries, address);
    } else if (sduDefaultValue) {
      setVolumeMdu(String(sduDefaultValue.volumeMdu));
      if (sduDefaultValue.existingBoxes) setExistingBoxes(sduDefaultValue.existingBoxes);
    }
  }

  /** Auto-compute volumes via single backend call (orchestrates Q1+2 then Q3 internally) */
  async function autoComputeVolumes(buildingIdStr: string): Promise<void> {
    try {
      const res = await fetchBuildingVolumes(buildingIdStr);
      const { entries, sduDefault: sduDef } = res.data;
      setBuildingGroupEntries(entries);
      setSduDefaultValue(sduDef);
    } catch {
      toast.info(t('tickets:addressWizard.noBuildingGroup'));
    }
  }

  /** Fill volumeMdu and existingBoxes form fields from matching BuildingGroupEntry */
  function fillVolumeFromEntries(entries: BuildingGroupEntry[], address: DmpAddressResponse) {
    const key = `${address.postalCode ?? ''}|${address.streetName ?? ''}|${address.houseNumberExtension ?? ''}`;
    const match = entries.find(
      (e) => `${e.postalCode}|${e.streetName}|${e.houseNumberExtension}` === key
    );
    if (match) {
      setVolumeMdu(String(match.volumeMdu));
      setExistingBoxes(match.existingBoxes);
    }
  }

  async function handleAddressSearch() {
    if (!searchStreet && !searchPostalCode && !searchHouseNumber) return;
    setSearchingAddress(true);
    setAddressSearchError('');
    setAddressSearchResults([]);
    try {
      const res = await searchDmpAddresses({
        street: searchStreet || undefined,
        postalCode: searchPostalCode || undefined,
        houseNumber: searchHouseNumber || undefined,
      });
      setAddressSearchResults(res.data.addresses);
      if (res.data.addresses.length === 0) {
        toast.info(t('tickets:addressWizard.noAddressSearch'));
      }
    } catch (err: any) {
      // ApiError has .status (HTTP status code)
      if (err?.status === 422) {
        setAddressSearchError(t('tickets:addressWizard.tooManyResults'));
        toast.warning(t('tickets:addressWizard.tooManyResults'));
      } else {
        toast.error(t('tickets:openOrder.addressSearchFailed'));
      }
    } finally {
      setSearchingAddress(false);
    }
  }

  function handleSubmitClick() {
    if (!subject.trim()) { toast.error(t('common:validation.subjectRequired')); return; }
    if (similarResults.length > 0 && !showSimilarConfirm) {
      setShowSimilarConfirm(true);
      return;
    }
    doSubmit();
  }

  async function doSubmit() {
    setShowSimilarConfirm(false);
    setSubmitting(true);
    try {
      const result = await createTicketWithExtras({
        ticketType: 'ADDRESS_MANAGEMENT',
        subject: subject.trim(),
        description: description.trim() || undefined,
        priority,
        jvid: jvid ? Number(jvid) : undefined,
        addrSource: source || undefined,
        addrAction: action || undefined,
        addrStatusField: addressStatus || undefined,
        addrScope: scope || undefined,
        addrStreet: streetName || undefined,
        addrHouseNumber: houseNumber || undefined,
        addrHouseNumberExt: houseNumberExt || undefined,
        addrBusNumber: busNumber || undefined,
        addrPostalCode: postalCode || undefined,
        addrMunicipality: municipality || undefined,
        addrCity: city || undefined,
        addrX: xLambert ? Number(xLambert) : undefined,
        addrY: yLambert ? Number(yLambert) : undefined,
        addrLat: lat ? Number(lat) : undefined,
        addrLon: lon ? Number(lon) : undefined,
        addrVolumeMdu: volumeMdu ? Number(volumeMdu) : undefined,
        addrExistingBoxes: existingBoxes || undefined,
        addrDwellingUnitType: dwellingUnitType || undefined,
        addrAddressSource: addressSource || undefined,
        addrDmpFetched: dmpFetched,
        addrBuildingGroupData: buildingGroupEntries.length > 0 ? buildingGroupEntries : undefined,
        belmapAddressId: belmapAddressId ? Number(belmapAddressId) : undefined,
        belmapBuildingId: belmapBuildingId ? Number(belmapBuildingId) : undefined,
        addrDmpSnapshot: (dmpOriginal as unknown as Record<string, unknown>) ?? undefined,
        // Customer contact
        contactFirstName: contactFirstName.trim() || undefined,
        contactLastName: contactLastName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      }, attachedFiles, pastedEmail);
      toast.success(t('tickets:create.created', { number: result.ticket.ticketNumber }));
      if (result.uploadErrors.length > 0) {
        toast.warning(t('tickets:customerEmail.someUploadsFailed', {
          count: result.uploadErrors.length,
          names: result.uploadErrors.map((e) => e.fileName).join(', '),
        }));
      }
      if (result.textAttachmentFailed) {
        toast.warning(t('tickets:customerEmail.textAttachmentFailed'));
      }
      navigate(`/tickets/${result.ticket.id}`);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:create.createError'));
    } finally {
      setSubmitting(false);
    }
  }

  const canProceedStep0 = source && action && addressStatus && scope;
  const canProceedStep1 = streetName.trim() !== '' && (postalCode.trim() !== '' || city.trim() !== '');
  const isCreation = action === 'Creation';
  const showJvidInput = !isCreation; // Information or Modification

  return (
    <div className="max-w-4xl mx-auto">
      <WizardStepper
        steps={[t('tickets:create.steps.classification'), t('tickets:create.steps.addressData'), t('tickets:create.steps.details'), t('tickets:create.steps.review')]}
        currentStep={step}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mt-8">
        {/* Step 0: Classification (PRD 4.2.5) */}
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:addressWizard.classificationTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:addressWizard.classificationHint')}</p>

            <div className="space-y-4 max-w-lg">
              <SelectField label={t('tickets:addressWizard.source')} value={source} onChange={setSource} options={addressSources} required placeholder={t('tickets:addressWizard.selectSource')} optionLabelFn={(opt) => t(`common:addressSource.${opt}`)} />

              <div>
                <SelectField
                  label={t('tickets:addressWizard.action')}
                  value={action}
                  onChange={setAction}
                  options={addressActions}
                  required
                  placeholder={t('tickets:addressWizard.selectAction')}
                  disabled={source === 'Other'}
                  optionLabelFn={(opt) => t(`common:addressAction.${opt}`)}
                />
                {source === 'Other' && (
                  <p className="text-xs text-amber-600 mt-1">{t('tickets:addressWizard.actionLocked')}</p>
                )}
              </div>

              <SelectField label={t('tickets:addressWizard.addressStatus')} value={addressStatus} onChange={setAddressStatus} options={addressStatuses} required placeholder={t('tickets:addressWizard.selectStatus')} optionLabelFn={(opt) => t(`common:addressStatus.${opt}`)} />
              <SelectField label={t('tickets:addressWizard.scope')} value={scope} onChange={setScope} options={addressScopes} required placeholder={t('tickets:addressWizard.selectScope')} optionLabelFn={(opt) => t(`common:addressScope.${opt}`)} />
            </div>
          </div>
        )}

        {/* Step 1: Address Data — all 17 fields (PRD 4.2.6) */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:addressWizard.addressDataTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">
              {isCreation
                ? t('tickets:addressWizard.addressDataHintLookup')
                : t('tickets:addressWizard.addressDataHintEntry')}
            </p>

            {/* Lookup section — conditional on action */}
            <div className="mb-4">
              {showJvidInput && (
                <div className="flex flex-col gap-4">
                  {/* JVID lookup */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1 max-w-xs">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        {t('tickets:addressWizard.jvidLabel')} <span className="text-slate-400 text-xs">{t('tickets:addressWizard.jvidHint')}</span>
                      </label>
                      <input
                        type="text"
                        value={jvid}
                        onChange={(e) => { setJvid(e.target.value); if (dmpFetched) setDmpFetched(false); }}
                        placeholder={t('tickets:openOrder.jvidPlaceholder')}
                        className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                      />
                    </div>
                    <button
                      onClick={handleDmpFetch}
                      disabled={jvid.length < 8 || fetchingDmp}
                      className="px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-purple to-brand-rose hover:from-brand-rose hover:to-brand-orange transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {fetchingDmp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      {fetchingDmp ? t('tickets:addressWizard.fetching') : t('tickets:addressWizard.fetchFromDmp')}
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3 max-w-lg">
                    <span className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs font-medium text-slate-400 uppercase">{t('tickets:addressWizard.or')}</span>
                    <span className="flex-1 h-px bg-slate-200" />
                  </div>
                </div>
              )}

              {/* Building Key lookup — always shown */}
              <div className={`flex items-end gap-3 ${showJvidInput ? 'mt-4' : ''}`}>
                <div className="flex-1 max-w-xs">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t('tickets:addressWizard.buildingKey')} <span className="text-slate-400 text-xs">{t('tickets:addressWizard.buildingKeyHint')}</span>
                  </label>
                  <input
                    type="text"
                    value={buildingKeyInput}
                    onChange={(e) => { setBuildingKeyInput(e.target.value); if (buildingFetched) { setBuildingFetched(false); setBuildingAddresses([]); } }}
                    placeholder="e.g. 12345678"
                    className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                  />
                </div>
                <button
                  onClick={handleBuildingKeyFetch}
                  disabled={!buildingKeyInput.trim() || fetchingBuilding}
                  className="px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-purple to-brand-rose hover:from-brand-rose hover:to-brand-orange transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {fetchingBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                  {fetchingBuilding ? t('tickets:openOrder.searching') : t('tickets:addressWizard.searchBuilding')}
                </button>
              </div>
            </div>

            {/* Divider — search by address */}
            <div className="flex items-center gap-3 my-4">
              <span className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-medium text-slate-400 uppercase">{t('tickets:openOrder.orSearchAddress')}</span>
              <span className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Address search inputs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:addressWizard.street')}</label>
                <input
                  type="text"
                  value={searchStreet}
                  onChange={(e) => setSearchStreet(e.target.value)}
                  placeholder={t('tickets:openOrder.streetPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:addressWizard.postalCode')}</label>
                <input
                  type="text"
                  value={searchPostalCode}
                  onChange={(e) => setSearchPostalCode(e.target.value)}
                  placeholder={t('tickets:openOrder.postalPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:addressWizard.houseNumber')}</label>
                <input
                  type="text"
                  value={searchHouseNumber}
                  onChange={(e) => setSearchHouseNumber(e.target.value)}
                  placeholder={t('tickets:openOrder.housePlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
                />
              </div>
            </div>
            <button
              onClick={handleAddressSearch}
              disabled={(!searchStreet && !searchPostalCode && !searchHouseNumber) || searchingAddress}
              className="mb-4 px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-purple to-brand-rose hover:from-brand-rose hover:to-brand-orange transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {searchingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {searchingAddress ? t('tickets:openOrder.searching') : t('tickets:openOrder.searchAddresses')}
            </button>

            {addressSearchError && (
              <div className="mb-4 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                {addressSearchError}
              </div>
            )}

            {/* Address search results picker */}
            {addressSearchResults.length > 0 && (
              <div className="mb-4">
                <AddressPickerTable
                  addresses={addressSearchResults}
                  selectedJvid={dmpFetched && dmpOriginal ? dmpOriginal.jvid : null}
                  onSelect={handleAddressSelect}
                />
              </div>
            )}

            {/* Address picker table (when building results exist) — enriched with volume columns */}
            {buildingAddresses.length > 0 && (
              <div className="mb-4">
                <AddressPickerTable
                  addresses={buildingAddresses}
                  selectedJvid={dmpFetched && dmpOriginal ? dmpOriginal.jvid : null}
                  onSelect={handleAddressSelect}
                  buildingGroupEntries={buildingGroupEntries.length > 0 ? buildingGroupEntries : undefined}
                  loadingVolumes={loadingBuildingData}
                />
              </div>
            )}

            {/* JVID read-only display (Creation mode, after address selected) */}
            {isCreation && dmpFetched && jvid && (
              <div className="flex items-center gap-2 px-4 py-2.5 mb-4 bg-slate-50 border border-slate-200 rounded-lg">
                <span className="text-sm text-slate-600">{t('tickets:addressWizard.jvidLabel')}:</span>
                <span className="font-mono font-semibold text-slate-800">{jvid}</span>
              </div>
            )}

            {/* DMP success badge */}
            {dmpFetched && (
              <div className="flex items-center gap-2 px-4 py-2.5 mb-6 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                <p className="text-sm text-green-700">
                  {t('tickets:addressWizard.dmpLoaded', { source: buildingFetched ? t('tickets:addressWizard.buildingKey') : t('tickets:addressWizard.jvidLabel') })} <span className="font-mono font-semibold">{buildingFetched ? buildingKeyInput : jvid}</span>
                </p>
              </div>
            )}

            {/* Group 1 — Identifiers */}
            <FieldGroupHeader title={t('tickets:addressWizard.identifiers')} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <InputField label={t('tickets:addressWizard.jvidLabel')} value={jvid} onChange={setJvid} placeholder={t('tickets:openOrder.jvidPlaceholder')} mono disabled={isCreation} />
              <InputField label={t('tickets:addressWizard.belmapAddressId')} value={belmapAddressId} onChange={setBelmapAddressId} placeholder="e.g. 98765432" mono />
              <InputField label={t('tickets:addressWizard.belmapBuildingId')} value={belmapBuildingId} onChange={setBelmapBuildingId} placeholder="e.g. 12345678" mono />
            </div>

            {/* Group 2 — Address */}
            <FieldGroupHeader title={t('tickets:addressWizard.address')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <InputField label={t('tickets:addressWizard.streetName')} value={streetName} onChange={setStreetName} required placeholder={t('tickets:openOrder.streetPlaceholder')} />
              </div>
              <InputField label={t('tickets:addressWizard.houseNumber')} value={houseNumber} onChange={setHouseNumber} placeholder={t('tickets:openOrder.housePlaceholder')} />
              <InputField label={t('tickets:addressWizard.extension')} value={houseNumberExt} onChange={setHouseNumberExt} placeholder="e.g. A" />
              <InputField label={t('tickets:addressWizard.busNumber')} value={busNumber} onChange={setBusNumber} placeholder="e.g. 3" />
              <InputField label={t('tickets:addressWizard.postalCode')} value={postalCode} onChange={setPostalCode} required placeholder={t('tickets:openOrder.postalPlaceholder')} />
              <InputField label={t('tickets:addressWizard.municipality')} value={municipality} onChange={setMunicipality} placeholder="e.g. Brussels" />
              <InputField label={t('tickets:addressWizard.city')} value={city} onChange={setCity} required placeholder="e.g. Brussels" />
            </div>
            <p className="text-xs text-muted-foreground -mt-3 mb-4">{t('tickets:addressWizard.postalCodeOrCityRequired')}</p>

            {/* Group 3 — Coordinates */}
            <FieldGroupHeader title={t('tickets:addressWizard.coordinates')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <InputField label={t('tickets:addressWizard.xLambert')} value={xLambert} onChange={setXLambert} placeholder="e.g. 150373.28" />
              <InputField label={t('tickets:addressWizard.yLambert')} value={yLambert} onChange={setYLambert} placeholder="e.g. 170666.12" />
              <InputField label={t('tickets:addressWizard.latitude')} value={lat} onChange={setLat} placeholder="e.g. 50.8467" />
              <InputField label={t('tickets:addressWizard.longitude')} value={lon} onChange={setLon} placeholder="e.g. 4.3517" />
            </div>

            {/* Group 4 — Building/Property */}
            <FieldGroupHeader title={t('tickets:addressWizard.buildingProperty')} />
            {dmpFetched && sduDefaultValue && buildingGroupEntries.length === 0 && (
              <p className="text-xs text-slate-400 -mt-2 mb-4">
                {t('tickets:addressWizard.sduDefault')} {sduDefaultValue.volumeMdu}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <InputField label={t('tickets:addressWizard.volumeMdu')} value={volumeMdu} onChange={setVolumeMdu} placeholder="e.g. 24" />
              <InputField label={t('tickets:addressWizard.existingBoxes')} value={existingBoxes} onChange={setExistingBoxes} placeholder="e.g. 2 fiber boxes installed" />
              <InputField label={t('tickets:addressWizard.dwellingUnitType')} value={dwellingUnitType} onChange={setDwellingUnitType} placeholder="e.g. apartment, house, commercial" />
              <InputField label={t('tickets:addressWizard.addressSource')} value={addressSource} onChange={setAddressSource} placeholder="e.g. Belmap, Operator" />
            </div>

            {/* DMP Reference Accordion (PRD 4.2.7) */}
            {dmpFetched && dmpOriginal && (
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setDmpRefOpen(!dmpRefOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
                >
                  <span className="text-sm font-semibold text-blue-800">{t('tickets:addressWizard.dmpReference')}</span>
                  <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${dmpRefOpen ? 'rotate-180' : ''}`} />
                </button>
                {dmpRefOpen && (
                  <div className="px-4 py-4 bg-blue-50/30">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3">
                      <DMPRefField label={t('tickets:addressWizard.jvidLabel')} value={String(dmpOriginal.jvid)} />
                      <DMPRefField label={t('tickets:addressWizard.belmapAddressId')} value={dmpOriginal.belmapAddressId != null ? String(dmpOriginal.belmapAddressId) : '—'} />
                      <DMPRefField label={t('tickets:addressWizard.belmapBuildingId')} value={dmpOriginal.belmapBuildingId != null ? String(dmpOriginal.belmapBuildingId) : '—'} />
                      <DMPRefField label={t('tickets:addressWizard.street')} value={dmpOriginal.streetName ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.houseNumber')} value={dmpOriginal.houseNumber ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.extension')} value={dmpOriginal.houseNumberExtension ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.postalCode')} value={dmpOriginal.postalCode ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.municipality')} value={dmpOriginal.municipality ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.city')} value={dmpOriginal.city ?? '—'} />
                      <DMPRefField label={t('tickets:addressWizard.latitude')} value={dmpOriginal.lat != null ? String(dmpOriginal.lat) : '—'} />
                      <DMPRefField label={t('tickets:addressWizard.longitude')} value={dmpOriginal.lon != null ? String(dmpOriginal.lon) : '—'} />
                      <DMPRefField label={t('tickets:addressWizard.volumeMdu')} value={dmpOriginal.volumeMdu != null ? String(dmpOriginal.volumeMdu) : '—'} />
                      <DMPRefField label={t('tickets:addressWizard.dwellingUnitType')} value={dmpOriginal.dwellingUnitType ?? '—'} />
                    </div>
                    <p className="text-xs text-blue-500 mt-3">{t('tickets:addressWizard.dmpReferenceHint')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Ticket details */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:addressWizard.ticketDetailsTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:addressWizard.ticketDetailsHint')}</p>

            <div className="space-y-4 max-w-lg">
              <InputField label={t('common:fields.subject')} value={subject} onChange={setSubject} required placeholder={t('tickets:addressWizard.subjectPlaceholder')} />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('common:fields.description')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('tickets:addressWizard.descriptionPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-none"
                />
              </div>
              <SelectField label={t('common:fields.priority')} value={priority} onChange={setPriority} options={['LOW', 'NORMAL', 'HIGH', 'URGENT']} optionLabelFn={(opt) => t(`common:priority.${opt}`)} />
            </div>

            <div className="mt-4 max-w-lg">
              <SimilarTicketsBanner
                subject={subject}
                description={description}
                jvid={jvid ? parseInt(jvid, 10) || undefined : undefined}
                ticketType="ADDRESS_MANAGEMENT"
                onResultsChange={setSimilarResults}
              />
            </div>

            {/* Customer email capture — paste + file attachments */}
            <div className="mt-6 max-w-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:customerEmail.label')}
                </label>
                <p className="text-xs text-slate-400 mb-2">{t('tickets:customerEmail.hint')}</p>
                <textarea
                  rows={6}
                  value={pastedEmail}
                  onChange={(e) => setPastedEmail(e.target.value)}
                  placeholder={t('tickets:customerEmail.placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono resize-y"
                />
              </div>
              <AttachmentPicker files={attachedFiles} onChange={setAttachedFiles} disabled={submitting} />
            </div>

            {/* Customer Contact — collapsible */}
            <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setContactOpen(!contactOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <span className="text-sm font-semibold text-slate-700">{t('tickets:addressWizard.customerContact')}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${contactOpen ? 'rotate-180' : ''}`} />
              </button>
              {contactOpen && (
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:addressWizard.customerContactHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
                    <InputField label={t('common:fields.contactFirstName')} value={contactFirstName} onChange={setContactFirstName} />
                    <InputField label={t('common:fields.contactLastName')} value={contactLastName} onChange={setContactLastName} />
                    <InputField label={t('common:fields.contactPhone')} value={contactPhone} onChange={setContactPhone} />
                    <InputField label={t('common:fields.contactEmail')} value={contactEmail} onChange={setContactEmail} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review (PRD 4.2.7) */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:addressWizard.reviewTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:addressWizard.reviewHint')}</p>

            {dmpFetched && (
              <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <p className="text-xs text-blue-700">{t('tickets:addressWizard.prefilledModified')}</p>
              </div>
            )}

            <div className="space-y-4">
              <ReviewSection title={t('tickets:detail.classification')}>
                <ReviewField label={t('tickets:addressWizard.source')} value={source ? t(`common:addressSource.${source}`) : '—'} />
                <ReviewField label={t('tickets:addressWizard.action')} value={action ? t(`common:addressAction.${action}`) : '—'} />
                <ReviewField label={t('tickets:addressWizard.addressStatus')} value={addressStatus ? t(`common:addressStatus.${addressStatus}`) : '—'} />
                <ReviewField label={t('tickets:addressWizard.scope')} value={scope ? t(`common:addressScope.${scope}`) : '—'} />
              </ReviewSection>

              <ReviewSection title={t('tickets:addressWizard.identifiers')}>
                <ReviewField label={t('tickets:addressWizard.jvidLabel')} value={jvid || 'N/A'} />
                <ReviewField label={t('tickets:addressWizard.belmapAddressId')} value={belmapAddressId || '—'} />
                <ReviewField label={t('tickets:addressWizard.belmapBuildingId')} value={belmapBuildingId || '—'} />
              </ReviewSection>

              <ReviewSection title={t('tickets:addressWizard.address')}>
                <ReviewField label={t('tickets:addressWizard.street')} value={`${streetName} ${houseNumber}${houseNumberExt ? houseNumberExt : ''}${busNumber ? ` bus ${busNumber}` : ''}`} />
                <ReviewField label={t('tickets:addressWizard.location')} value={`${postalCode} ${municipality}${city ? `, ${city}` : ''}`} />
              </ReviewSection>

              <ReviewSection title={t('tickets:addressWizard.coordinates')}>
                <ReviewField label={t('tickets:addressWizard.xLambert')} value={xLambert || '—'} />
                <ReviewField label={t('tickets:addressWizard.yLambert')} value={yLambert || '—'} />
                <ReviewField label={t('tickets:addressWizard.latitude')} value={lat || '—'} />
                <ReviewField label={t('tickets:addressWizard.longitude')} value={lon || '—'} />
              </ReviewSection>

              <ReviewSection title={t('tickets:addressWizard.buildingProperty')}>
                <ReviewField label={t('tickets:addressWizard.volumeMdu')} value={volumeMdu || '—'} />
                <ReviewField label={t('tickets:addressWizard.existingBoxes')} value={existingBoxes || '—'} />
                <ReviewField label={t('tickets:addressWizard.dwellingUnitType')} value={dwellingUnitType || '—'} />
                <ReviewField label={t('tickets:addressWizard.addressSource')} value={addressSource || '—'} />
              </ReviewSection>

              {buildingGroupEntries.length > 0 && (
                <ReviewSection title={t('tickets:detail.buildingGroup')}>
                  <ReviewField label="Entries" value={`${buildingGroupEntries.length} (${buildingGroupEntries.filter(e => e.role === 'Main').length} main, ${buildingGroupEntries.filter(e => e.role === 'Child').length} children)`} />
                  <ReviewField label={t('tickets:addressWizard.volumeMdu')} value={String(buildingGroupEntries.reduce((sum, e) => sum + e.volumeMdu, 0))} />
                </ReviewSection>
              )}

              {(contactFirstName || contactLastName || contactPhone || contactEmail) && (
                <ReviewSection title={t('tickets:addressWizard.customerContact')}>
                  <ReviewField label={t('common:fields.contactFirstName')} value={contactFirstName || '—'} />
                  <ReviewField label={t('common:fields.contactLastName')} value={contactLastName || '—'} />
                  <ReviewField label={t('common:fields.contactPhone')} value={contactPhone || '—'} />
                  <ReviewField label={t('common:fields.contactEmail')} value={contactEmail || '—'} />
                </ReviewSection>
              )}

              <ReviewSection title={t('tickets:addressWizard.ticketDetailsTitle')}>
                <ReviewField label={t('common:fields.type')} value={t('common:ticketType.ADDRESS_MANAGEMENT')} />
                <ReviewField label={t('common:fields.subject')} value={subject || '—'} />
                <div className="col-span-2">
                  <ReviewField label={t('common:fields.description')} value={description || '—'} />
                </div>
                <ReviewField label={t('common:fields.priority')} value={t(`common:priority.${priority}`)} />
              </ReviewSection>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <div>
          {step === 0 ? (
            <Link to="/tickets/new" className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.cancel')}
            </Link>
          ) : (
            <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.previous')}
            </button>
          )}
        </div>
        <div>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 0 && !canProceedStep0) || (step === 1 && !canProceedStep1)}
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('common:actions.next')}
            </button>
          ) : (
            <button onClick={handleSubmitClick} disabled={submitting} className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? t('tickets:create.creating') : t('tickets:create.submitTicket')}
            </button>
          )}
        </div>
      </div>

      {/* Glassmorphism loading modal */}
      {loadingBuildingData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8 max-w-md mx-4 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-brand-rose mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              {t('tickets:addressWizard.searchingBuilding')}
            </h3>
            <p className="text-sm text-slate-500">
              {t('tickets:addressWizard.fetchingBuilding')}
            </p>
            <p className="text-xs text-slate-400 mt-2">{t('tickets:addressWizard.pleaseWait')}</p>
          </div>
        </div>
      )}

      {showSimilarConfirm && similarResults.length > 0 && (
        <SimilarTicketsConfirmDialog
          results={similarResults}
          onConfirm={doSubmit}
          onCancel={() => setShowSimilarConfirm(false)}
        />
      )}
    </div>
  );
}

// ── Reusable field components ────────────────────────────────────

function FieldGroupHeader({ title }: { title: string }) {
  return (
    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 mt-2 flex items-center gap-2">
      <span className="w-6 h-px bg-slate-200" />
      {title}
      <span className="flex-1 h-px bg-slate-200" />
    </h3>
  );
}

function InputField({ label, value, onChange, required, placeholder, mono, disabled }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean; placeholder?: string; mono?: boolean; disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 ${mono ? 'font-mono' : ''} ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, placeholder, disabled, optionLabelFn }: {
  label: string; value: string; onChange: (v: string) => void; options: readonly string[]; required?: boolean; placeholder?: string; disabled?: boolean; optionLabelFn?: (opt: string) => string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt} value={opt}>{optionLabelFn ? optionLabelFn(opt) : opt}</option>
        ))}
      </select>
    </div>
  );
}

function DMPRefField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-0.5">{label}</p>
      <p className="text-sm text-blue-700 font-mono">{value}</p>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700">{value}</p>
    </div>
  );
}
